import { useParticipants } from "@livekit/components-react";
import classNames from "./MeetingAttendees.module.css";
import { LocalParticipant, RemoteParticipant } from "livekit-client";
import api from "../../../hooks/api-new";
import { useAxios } from "../../../hooks/axios";
import { FiMic, FiMicOff } from "react-icons/fi";
import { useMemo } from "react";
import { useRoom } from "../../context/room/room";
import LanguageSelect from "../../common/LanguageSelect";
import useUser from "../../../hooks/user";
import { IoClose } from "react-icons/io5";
import { useMeeting } from "../../../hooks/meeting";
import { StatelessToggle } from "../../common/Toggle";
import { getLanguageName } from "../../../types";
import SimpleBar from "simplebar-react";

export default function MeetingAttendees() {
  const { room, organization } = useRoom();
  const participants = useParticipants();

  const axios = useAxios();
  const updateParticipant = api.useUpdateRoomParticipant(axios);

  const [user] = useUser();
  const [, setMeeting] = useMeeting();

  const allParticipantsAIVoiceEnabled = useMemo(() => {
    return participants.reduce(
      (acc: boolean, curr: RemoteParticipant | LocalParticipant) => {
        if (curr.isAgent) {
          return acc && true;
        }

        if (!curr.metadata) {
          return acc && true;
        }

        const metadata = JSON.parse(curr.metadata) as { voice?: string };
        return acc && !!metadata.voice;
      },
      true
    );
  }, [participants]);

  if (!room || !organization) {
    return null;
  }

  return (
    <div className={classNames.MeetingAttendees}>
      <div className={classNames.Header}>
        <h3>{`Participants (${
          participants.filter((p) => !p.isAgent).length
        })`}</h3>
        <button
          onClick={() =>
            setMeeting((prevState) => ({
              ...prevState,
              layout: {
                ...prevState.layout,
                panels: { ...prevState.layout.panels, side: undefined },
              },
            }))
          }
        >
          <IoClose />
        </button>
      </div>

      <div style={{ display: "flex", overflow: "auto" }}>
        <SimpleBar style={{ width: "100%", height: "100%" }}>
          <div className={classNames.AttendeeList}>
            {participants.map((participant, index) => {
              const metadata = participant.metadata;
              if (!metadata) {
                return;
              }
              const parsedMetadata = JSON.parse(metadata) as {
                name: string;
                language: string;
              };

              return (
                <div key={index} className={classNames.Attendee}>
                  <div>
                    {parsedMetadata.name}
                    {participant.identity === user.livekitId
                      ? " (You)"
                      : undefined}
                    {room.host_id === user.auth?.id ? (
                      <LanguageSelect
                        size="x-small"
                        color="gray"
                        backgroundColor="black"
                        borderColor="dark-gray"
                        languages={[
                          ...(organization.autodetect_enabled
                            ? ["autodetect"]
                            : []),
                          ...room.languages,
                        ]}
                        selectedLanguage={parsedMetadata.language}
                        onChange={(value) => {
                          updateParticipant({
                            room_id: room.id,
                            participant_identity: participant.identity,
                            language: value,
                          });
                        }}
                      />
                    ) : (
                      <div>{getLanguageName(parsedMetadata.language)}</div>
                    )}
                  </div>
                  {room.host_id === user.auth?.id ? (
                    <button
                      onClick={() => {
                        updateParticipant({
                          room_id: room.id,
                          participant_identity: participant.identity,
                          muted: true,
                        });
                      }}
                    >
                      {participant.isMicrophoneEnabled ? (
                        <FiMic />
                      ) : (
                        <FiMicOff />
                      )}
                    </button>
                  ) : (
                    <div>
                      {participant.isMicrophoneEnabled ? (
                        <FiMic />
                      ) : (
                        <FiMicOff />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </SimpleBar>
      </div>

      {organization.voice_translation_enabled &&
        (room.host_id === user.auth?.id ? (
          <button
            className={classNames.EnableVoiceForAll}
            onClick={() => {
              participants.forEach((p) => {
                updateParticipant({
                  room_id: room.id,
                  participant_identity: p.identity,
                  ai_voice_enabled: !allParticipantsAIVoiceEnabled,
                });
              });
            }}
          >
            <StatelessToggle enabled={allParticipantsAIVoiceEnabled} />
            Enable voice translation for all
          </button>
        ) : (
          <div className={classNames.EnableVoiceForAll}>
            <StatelessToggle
              noPointer={true}
              enabled={allParticipantsAIVoiceEnabled}
            />
            Voice translation enabled for all
          </div>
        ))}
    </div>
  );
}
